/**
 * Phase 2L-A145 — PWS Franchisee Partner Interest admin list.
 *
 * URL: /admin/pws-franchisee-interest
 *
 * Read-only table with:
 *   • Status dropdown per row (existing since A145).
 *   • "Convert to Trade Partner" row-level action (A145-fu-A, Feb 2026):
 *     safely converts a PWS application into a live partner record,
 *     handling the two-shot duplicate flow the backend expects.
 *
 * Deep-linking: this page respects the `?status=<value>` query so the
 * dashboard tile can pre-filter the table.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, Mail, Phone, MapPin, RefreshCw, Filter, Handshake,
  UserPlus, CheckCircle2, AlertTriangle, ExternalLink, Link2, Send,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";

const STATUS_STYLES = {
  new:             { label: "New",             color: "bg-amber-100  text-amber-800" },
  contacted:       { label: "Contacted",       color: "bg-cyan-100   text-cyan-800" },
  onboarded:       { label: "Onboarded",       color: "bg-emerald-100 text-emerald-800" },
  not_proceeding:  { label: "Not proceeding",  color: "bg-slate-200  text-slate-700" },
  archived:        { label: "Archived",        color: "bg-slate-100  text-slate-500" },
};
const STATUS_ORDER = ["new", "contacted", "onboarded", "not_proceeding", "archived"];

const INTEREST_LABELS = {
  join:                     "Ready to join",
  more_info:                "Wants more info",
  test_kit_workflow_first:  "Test kit workflow first",
};

export default function AdminPwsFranchiseeInterest() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [savingId, setSavingId] = useState(null);
  const [convertModal, setConvertModal] = useState(null); // { lead, existing_partner? }
  const [promoModal, setPromoModal] = useState(null);     // { lead? , initialEmail?, initialFirstName? }

  const load = () => {
    setLoading(true);
    api.get("/admin/pws-franchisee-interest?limit=500")
      .then(({ data }) => setLeads(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(formatApiError(e) || "Failed to load leads"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Keep the URL `?status=` in sync so the dashboard tile deep-link
  // and any bookmarks survive a page reload.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (statusFilter === "all") next.delete("status");
    else next.set("status", statusFilter);
    setSearchParams(next, { replace: true });
  }, [statusFilter]);

  const counts = useMemo(() => {
    const acc = { all: leads.length, invited: 0 };
    for (const s of STATUS_ORDER) acc[s] = 0;
    for (const l of leads) {
      if (l.status) acc[l.status] = (acc[l.status] || 0) + 1;
      // SCWT-INVITED-FILTER · Feb 2026 — "invited & awaiting response"
      // is a cross-cutting bucket (independent of status). Count any
      // lead with `promo_email_sent_at` set that is NOT already
      // converted. Once they convert, they collapse to the
      // "Converted → Partner" strip and no longer belong here.
      if (l.promo_email_sent_at && !l.converted_partner_id) {
        acc.invited += 1;
      }
    }
    return acc;
  }, [leads]);

  const visible = statusFilter === "all"
    ? leads
    : statusFilter === "invited"
      ? leads.filter((l) => l.promo_email_sent_at && !l.converted_partner_id)
      : leads.filter((l) => l.status === statusFilter);

  const updateStatus = async (id, next) => {
    setSavingId(id);
    try {
      await api.patch(`/admin/pws-franchisee-interest/${id}/status`, { status: next });
      setLeads((rows) => rows.map((r) => r.id === id ? { ...r, status: next } : r));
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e) || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <PortalShell title="PWS Franchisee Partner Interest">
      <div data-testid="admin-pws-franchisee-interest-page" className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Handshake className="w-6 h-6 text-cyan-600" /> PWS Franchisee Partner Interest
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Leads submitted from the public <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">/pws-franchisee-network</code> page. Tag each lead as you work through them, then use <strong>Convert to Trade Partner</strong> to create a live partner record.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={() => setPromoModal({})}
              data-testid="admin-pws-fi-open-promo-email-btn"
              className="h-10 bg-cyan-600 hover:bg-cyan-700"
            >
              <Send className="w-4 h-4 mr-2" /> Send invite email
            </Button>
            <Button
              variant="outline"
              onClick={load}
              data-testid="admin-pws-fi-reload"
              className="h-10"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Status filter chip strip */}
        <div className="flex flex-wrap gap-2" data-testid="admin-pws-fi-status-strip">
          <button
            onClick={() => setStatusFilter("all")}
            data-testid="admin-pws-fi-filter-all"
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              statusFilter === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            All <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${statusFilter === "all" ? "bg-white/20 text-white" : "bg-white text-slate-600"}`}>{counts.all || 0}</span>
          </button>
          {STATUS_ORDER.map((s) => {
            const cfg = STATUS_STYLES[s];
            const active = statusFilter === s;
            const c = counts[s] || 0;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`admin-pws-fi-filter-${s}`}
                disabled={c === 0}
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  active ? "bg-slate-900 text-white" : c === 0 ? "bg-slate-50 text-slate-400 cursor-not-allowed" : `${cfg.color} hover:brightness-95`
                }`}
              >
                {cfg.label} <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? "bg-white/20 text-white" : "bg-white/70 text-slate-700"}`}>{c}</span>
              </button>
            );
          })}
          {/* SCWT-INVITED-FILTER · Feb 2026 — cross-cutting
              "invited & awaiting response" bucket, sits at the end of
              the filter strip and is styled amber so it stands out
              from status filters. Disabled state (grey, no cursor)
              when no leads currently have `promo_email_sent_at`. */}
          {(() => {
            const c = counts.invited || 0;
            const active = statusFilter === "invited";
            return (
              <button
                onClick={() => setStatusFilter("invited")}
                data-testid="admin-pws-fi-filter-invited"
                disabled={c === 0}
                title="Leads that have received the PWS Partner Network invite email and have not yet converted"
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ml-1 ${
                  active
                    ? "bg-amber-600 text-white ring-2 ring-amber-300"
                    : c === 0
                      ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                      : "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200"
                }`}
              >
                <Send className="w-3.5 h-3.5" />
                Invited — awaiting response
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                  active ? "bg-white/20 text-white" : "bg-white/70 text-amber-800"
                }`}>{c}</span>
              </button>
            );
          })()}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : visible.length === 0 ? (
            <div data-testid="admin-pws-fi-empty" className="p-16 text-center text-slate-500">
              <Filter className="w-6 h-6 mx-auto text-slate-300 mb-3" />
              {statusFilter === "invited"
                ? "No invites currently awaiting a response — either they've all converted, or you haven't sent any yet."
                : statusFilter !== "all"
                  ? `No leads in ${STATUS_STYLES[statusFilter]?.label}.`
                  : "No leads yet."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Business / Contact</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Region</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Interest</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Received</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Partner</th>
                </tr>
              </thead>
              <tbody data-testid="admin-pws-fi-table-body">
                {visible.map((l) => (
                  <LeadRow
                    key={l.id}
                    lead={l}
                    saving={savingId === l.id}
                    onStatusChange={(next) => updateStatus(l.id, next)}
                    onOpenConvert={() => setConvertModal({ lead: l })}
                    onOpenPromo={() => setPromoModal({ lead: l })}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {convertModal && (
        <ConvertModal
          state={convertModal}
          onClose={(mutated) => {
            setConvertModal(null);
            if (mutated) load();
          }}
        />
      )}
      {promoModal && (
        <PromoEmailModal
          initialEmail={promoModal.lead?.email || ""}
          initialFirstName={extractFirstName(promoModal.lead?.contact_name)}
          leadId={promoModal.lead?.id}
          onClose={(sent) => {
            setPromoModal(null);
            if (sent) load();
          }}
        />
      )}
    </PortalShell>
  );
}


// Helper — split a "Jane Smith" contact_name to "Jane" for the {{first_name}}
// placeholder. Falls back to "there" so the greeting always reads naturally.
function extractFirstName(contactName) {
  if (!contactName) return "";
  const trimmed = String(contactName).trim();
  if (!trimmed) return "";
  return trimmed.split(/\s+/)[0];
}


// ─── Row ────────────────────────────────────────────────────────────
function LeadRow({ lead, saving, onStatusChange, onOpenConvert, onOpenPromo }) {
  const converted = !!lead.converted_partner_id;
  const invited   = !!lead.promo_email_sent_at;

  // Feb 2026 (Issue #2) — Once a lead has been converted to a live
  // partner, collapse the entire row into a single compact "converted"
  // strip so it visually recedes and no longer looks like an active
  // unresolved lead. Historical interest type is kept as small grey
  // caption for audit context, per the user brief.
  if (converted) {
    return (
      <tr
        data-testid={`admin-pws-fi-row-${lead.id}`}
        className="border-b border-slate-100 last:border-0 bg-emerald-50/30 hover:bg-emerald-50/50"
      >
        <td colSpan={6} className="p-3">
          <div
            data-testid={`admin-pws-fi-converted-strip-${lead.id}`}
            className="flex items-center justify-between gap-3 flex-wrap"
          >
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <span
                data-testid={`admin-pws-fi-converted-badge-${lead.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 whitespace-nowrap"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Converted → Partner
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate max-w-[320px]">
                  {lead.business_name}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  <MapPin className="w-2.5 h-2.5 inline-block mr-0.5 -mt-0.5 text-slate-400" />
                  {lead.region}
                  <span className="mx-2 text-slate-300">·</span>
                  Originally: <span className="italic">{INTEREST_LABELS[lead.interest_type] || lead.interest_type || "—"}</span>
                  <span className="mx-2 text-slate-300">·</span>
                  Received {formatDateAU(lead.created_at)}
                  {lead.converted_at && (
                    <>
                      <span className="mx-2 text-slate-300">·</span>
                      Converted {formatDateAU(lead.converted_at)}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={`/admin/partners?open=${lead.converted_partner_id}`}
                data-testid={`admin-pws-fi-partner-link-${lead.id}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold text-emerald-800 bg-white border border-emerald-200 hover:bg-emerald-50"
              >
                View partner <ExternalLink className="w-3 h-3" />
              </a>
              <TradePortalInviteAction
                partnerId={lead.converted_partner_id}
                leadId={lead.id}
              />
            </div>
          </div>
          {/* Feb 2026 (user follow-up): keep the original enquiry note
              reachable on converted rows, but visually subtle so the
              row still recedes. Collapsed by default. */}
          {lead.message && (
            <details
              data-testid={`admin-pws-fi-converted-note-${lead.id}`}
              className="mt-2 text-[11px] text-slate-500 max-w-3xl"
            >
              <summary
                data-testid={`admin-pws-fi-converted-note-toggle-${lead.id}`}
                className="cursor-pointer inline-flex items-center gap-1 text-slate-500 hover:text-slate-700"
              >
                <span className="text-emerald-700/70">▸</span> View original note
              </summary>
              <div
                data-testid={`admin-pws-fi-converted-note-body-${lead.id}`}
                className="mt-1.5 rounded bg-white/70 border border-emerald-100 p-2.5 whitespace-pre-wrap text-slate-700"
              >
                {lead.message}
              </div>
            </details>
          )}
        </td>
      </tr>
    );
  }

  return (
    <tr data-testid={`admin-pws-fi-row-${lead.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
      <td className="p-4 align-top">
        <div className="font-semibold text-slate-900">{lead.business_name}</div>
        <div className="text-xs text-slate-600 mt-0.5">{lead.contact_name}</div>
        <div className="text-xs text-slate-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-cyan-700">
            <Mail className="w-3 h-3" /> {lead.email}
          </a>
          <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-cyan-700">
            <Phone className="w-3 h-3" /> {lead.phone}
          </a>
        </div>
        {lead.message && (
          <details className="mt-2 text-xs text-slate-600 max-w-md">
            <summary className="cursor-pointer text-cyan-700 font-semibold">View message</summary>
            <div className="mt-2 rounded bg-slate-50 p-2.5 whitespace-pre-wrap">{lead.message}</div>
          </details>
        )}
      </td>
      <td className="p-4 align-top">
        <span className="inline-flex items-center gap-1 text-xs text-slate-700">
          <MapPin className="w-3 h-3 text-slate-400" /> {lead.region}
        </span>
      </td>
      <td className="p-4 align-top">
        <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700">
          {INTEREST_LABELS[lead.interest_type] || lead.interest_type}
        </span>
      </td>
      <td className="p-4 align-top text-xs text-slate-600">
        {formatDateAU(lead.created_at, { withTime: true })}
      </td>
      <td className="p-4 align-top">
        <select
          value={lead.status || "new"}
          onChange={(e) => onStatusChange(e.target.value)}
          disabled={saving}
          data-testid={`admin-pws-fi-status-select-${lead.id}`}
          className={`text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer disabled:opacity-50 ${STATUS_STYLES[lead.status || "new"]?.color || "bg-slate-100 text-slate-700"}`}
        >
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>{STATUS_STYLES[s].label}</option>
          ))}
        </select>
        {lead.status === "onboarded" && !converted && (
          <div className="mt-1.5 text-[10px] text-amber-700 leading-tight" data-testid={`admin-pws-fi-onboarded-uncoverted-${lead.id}`}>
            Tagged onboarded — not yet converted.
          </div>
        )}
      </td>
      <td className="p-4 align-top">
        <div className="flex flex-col items-start gap-1.5">
          <button
            type="button"
            onClick={onOpenConvert}
            data-testid={`admin-pws-fi-convert-btn-${lead.id}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-cyan-600 text-white hover:bg-cyan-700 shadow-sm"
          >
            <UserPlus className="w-3 h-3" /> Convert to Trade Partner
          </button>
          <button
            type="button"
            onClick={onOpenPromo}
            data-testid={`admin-pws-fi-send-invite-btn-${lead.id}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
            title={invited ? "Send invite email again (already sent)" : "Send PWS Franchisee Network invite email"}
          >
            <Send className="w-3 h-3" />
            {invited ? "Resend invite" : "Send invite"}
          </button>
          {invited && (
            <span
              data-testid={`admin-pws-fi-invited-badge-${lead.id}`}
              className="inline-flex items-center gap-1 text-[10px] text-emerald-700"
            >
              <CheckCircle2 className="w-3 h-3" /> invite sent
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}


// ─── Trade Portal invite action (converted rows only) ──────────────
// Small inline widget for the LeadRow. Fetches invite status for the
// converted partner_id, renders a "Send Trade Portal invite" / "Resend
// invite" button, and handles the orphan-user 409 by prompting the
// admin to attach + resend.
function TradePortalInviteAction({ partnerId, leadId }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orphanPrompt, setOrphanPrompt] = useState(null);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/trade-portal-invite-status`);
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => { fetchStatus(); }, [partnerId]);

  const send = async (linkExistingUser = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/partners/${partnerId}/trade-portal-invite`,
        { link_existing_user: !!linkExistingUser },
      );
      if (data.ok) {
        toast.success(
          data.is_resend
            ? `Trade Portal invite resent (#${data.invite_count}).`
            : "Trade Portal invite sent."
        );
      } else {
        toast.error(`Send failed: ${data.send_error || data.send_status}`);
      }
      setOrphanPrompt(null);
      await fetchStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "existing_user_orphan") {
        setOrphanPrompt({
          existing_user_id:   detail.existing_user_id,
          existing_user_role: detail.existing_user_role,
        });
      } else {
        const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Failed to send invite";
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <span
        data-testid={`admin-pws-fi-trade-portal-loading-${leadId}`}
        className="text-[10px] text-slate-400"
      >
        checking invite status…
      </span>
    );
  }
  if (!status.partner_type_eligible) return null;

  const alreadySent = !!status.trade_portal_invite_sent_at;
  const isSent = status.trade_portal_invite_status === "sent";
  const isFailed = status.trade_portal_invite_status && status.trade_portal_invite_status !== "sent";
  const disabled = busy || !!status.partner_blocked || !status.email;

  return (
    <div className="flex flex-col items-start gap-1.5">
      <button
        type="button"
        disabled={disabled}
        onClick={() => send(false)}
        data-testid={
          alreadySent
            ? `admin-pws-fi-trade-portal-resend-btn-${leadId}`
            : `admin-pws-fi-trade-portal-send-btn-${leadId}`
        }
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
        title={
          !status.email
            ? "No email on file"
            : status.partner_blocked
              ? `Partner status is ${status.partner_status} — unblock first`
              : alreadySent
                ? "Resend Trade Portal login invite"
                : "Send Trade Portal login invite"
        }
      >
        {alreadySent ? <RefreshCw className="w-3 h-3" /> : <Send className="w-3 h-3" />}
        {busy ? "Sending…" : alreadySent ? "Resend invite" : "Send Trade Portal invite"}
      </button>
      {alreadySent && isSent && (
        <span
          data-testid={`admin-pws-fi-trade-portal-sent-badge-${leadId}`}
          className="inline-flex items-center gap-1 text-[10px] text-emerald-700"
        >
          <CheckCircle2 className="w-3 h-3" /> invite sent
          {status.trade_portal_invite_count > 1 && (
            <span className="text-slate-500">(#{status.trade_portal_invite_count})</span>
          )}
        </span>
      )}
      {isFailed && (
        <span
          data-testid={`admin-pws-fi-trade-portal-failed-badge-${leadId}`}
          className="inline-flex items-center gap-1 text-[10px] text-rose-700"
          title={status.trade_portal_invite_last_error || ""}
        >
          <AlertTriangle className="w-3 h-3" /> send failed
        </span>
      )}
      {orphanPrompt && (
        <div
          data-testid={`admin-pws-fi-trade-portal-orphan-prompt-${leadId}`}
          className="mt-1 rounded border border-amber-300 bg-amber-50 p-2 text-[10px] text-amber-900 leading-relaxed max-w-[260px]"
        >
          A user row already exists for this email but isn&apos;t linked. Attach and send?
          <div className="mt-1 flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => send(true)}
              data-testid={`admin-pws-fi-trade-portal-orphan-confirm-btn-${leadId}`}
              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-600 hover:bg-amber-700 text-white"
            >
              Attach and send
            </button>
            <button
              type="button"
              onClick={() => setOrphanPrompt(null)}
              data-testid={`admin-pws-fi-trade-portal-orphan-cancel-btn-${leadId}`}
              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-white border border-amber-300 text-amber-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Convert modal ──────────────────────────────────────────────────
function ConvertModal({ state, onClose }) {
  const { lead } = state;
  const [phase, setPhase] = useState("confirm"); // confirm | duplicate | success | error
  const [busy, setBusy] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // {existing_partner}
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const runConvert = async (linkExistingId = null) => {
    setBusy(true);
    setErrorMsg("");
    try {
      const url = linkExistingId
        ? `/admin/pws-franchisee-interest/${lead.id}/convert?link_existing=${encodeURIComponent(linkExistingId)}`
        : `/admin/pws-franchisee-interest/${lead.id}/convert`;
      const { data } = await api.post(url);
      setResult(data);
      setPhase("success");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // detail may be either a string (simple error) or a dict (our
      // structured duplicate_partner_email / already_converted paths).
      if (detail && typeof detail === "object" && detail.code === "duplicate_partner_email") {
        setDuplicate(detail);
        setPhase("duplicate");
      } else if (detail && typeof detail === "object" && detail.code === "already_converted") {
        setErrorMsg(detail.message || "Already converted");
        setResult({ partner_id: detail.converted_partner_id });
        setPhase("error");
      } else {
        setErrorMsg(formatApiError(e) || "Conversion failed");
        setPhase("error");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-pws-fi-convert-modal"
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onClose(phase === "success")}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="p-5 border-b border-slate-200 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-4 h-4 text-cyan-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 text-base">
              Convert to Trade Partner
            </h3>
            <div className="text-xs text-slate-600 mt-0.5">
              {lead.business_name} · {lead.email}
            </div>
          </div>
        </div>

        <div className="p-5">
          {phase === "confirm" && (
            <div data-testid="admin-pws-fi-convert-confirm">
              <p className="text-sm text-slate-700 leading-relaxed">
                Convert this PWS application into a live trade partner? This will
                create a partner record and make the partner eligible for
                attribution and commissions.
              </p>
              <ul className="mt-4 space-y-1.5 text-xs text-slate-600 list-disc pl-5">
                <li>Partner type: <code className="text-[11px] bg-slate-100 px-1 rounded">pws_franchisee</code></li>
                <li>Commission enabled: <strong>Yes</strong> (opt-out on the partner detail if needed)</li>
                <li>PWS partner status: <strong>Active</strong></li>
                <li>Unique <code className="text-[11px] bg-slate-100 px-1 rounded">partner_slug</code> generated from the region</li>
                <li>Lead status → <strong>Onboarded</strong>, stamped with converted_by / converted_at</li>
              </ul>
            </div>
          )}

          {phase === "duplicate" && duplicate && (
            <div data-testid="admin-pws-fi-convert-duplicate">
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  A partner already exists with this email. You can link this
                  application to the existing partner (no new partner will be
                  created), or cancel and fix the duplicate first.
                </div>
              </div>
              <div className="mt-4 rounded-lg border border-slate-200 p-3 text-xs">
                <div className="font-semibold text-slate-900">{duplicate.existing_partner?.business_name || "(no business name)"}</div>
                <div className="text-slate-600 mt-0.5">{duplicate.existing_partner?.email}</div>
                <div className="mt-1.5 text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>type: <code className="text-[10px] bg-slate-100 px-1 rounded">{duplicate.existing_partner?.partner_type || "—"}</code></span>
                  <span>status: <code className="text-[10px] bg-slate-100 px-1 rounded">{duplicate.existing_partner?.status || "—"}</code></span>
                  {duplicate.existing_partner?.partner_slug && (
                    <span>slug: <code className="text-[10px] bg-slate-100 px-1 rounded">{duplicate.existing_partner.partner_slug}</code></span>
                  )}
                </div>
              </div>
            </div>
          )}

          {phase === "success" && result && (
            <div data-testid="admin-pws-fi-convert-success">
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-900 leading-relaxed">
                  <div className="font-semibold">
                    {result.conversion_note === "linked_to_existing_partner"
                      ? "Linked to existing partner."
                      : "Partner created and commission-enabled."}
                  </div>
                  <div className="mt-1">
                    Lead is now <strong>onboarded</strong> and stamped with
                    converted_by / converted_at. The partner is eligible for
                    attribution and commissions.
                  </div>
                </div>
              </div>

              {result.partner && (
                <div className="mt-4 rounded-lg border border-slate-200 p-3 text-xs">
                  <div className="font-semibold text-slate-900">{result.partner.business_name}</div>
                  <div className="text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                    <span>id: <code className="text-[10px] bg-slate-100 px-1 rounded">{result.partner_id}</code></span>
                    <span>slug: <code className="text-[10px] bg-slate-100 px-1 rounded">{result.partner.partner_slug || "—"}</code></span>
                  </div>
                </div>
              )}

              {result.trade_portal_invite_note && (
                <div className="mt-3 rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-[11px] text-cyan-900 leading-relaxed" data-testid="admin-pws-fi-convert-invite-note">
                  <strong>Trade Portal invite:</strong> {result.trade_portal_invite_note}
                </div>
              )}
            </div>
          )}

          {phase === "error" && (
            <div data-testid="admin-pws-fi-convert-error" className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900 leading-relaxed">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">{errorMsg}</div>
                  {result?.partner_id && (
                    <div className="mt-1">
                      Existing partner id:{" "}
                      <a
                        href={`/admin/partners?open=${result.partner_id}`}
                        className="underline font-semibold"
                        data-testid="admin-pws-fi-convert-error-partner-link"
                      >
                        {result.partner_id}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
          {phase === "confirm" && (
            <>
              <Button
                variant="outline"
                onClick={() => onClose(false)}
                disabled={busy}
                data-testid="admin-pws-fi-convert-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => runConvert(null)}
                disabled={busy}
                data-testid="admin-pws-fi-convert-confirm-btn"
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                {busy ? "Converting…" : "Convert to Trade Partner"}
              </Button>
            </>
          )}
          {phase === "duplicate" && (
            <>
              <Button
                variant="outline"
                onClick={() => onClose(false)}
                disabled={busy}
                data-testid="admin-pws-fi-convert-duplicate-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => runConvert(duplicate.existing_partner.id)}
                disabled={busy}
                data-testid="admin-pws-fi-convert-link-existing-btn"
                className="bg-slate-800 hover:bg-slate-900"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                {busy ? "Linking…" : "Link to existing partner"}
              </Button>
            </>
          )}
          {(phase === "success" || phase === "error") && (
            <>
              {result?.partner_id && (
                <a
                  href={`/admin/partners?open=${result.partner_id}`}
                  data-testid="admin-pws-fi-convert-open-partner-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold bg-cyan-600 text-white hover:bg-cyan-700"
                >
                  <ExternalLink className="w-4 h-4" /> Open partner record
                </a>
              )}
              <Button
                variant="outline"
                onClick={() => onClose(phase === "success")}
                data-testid="admin-pws-fi-convert-close-btn"
              >
                Close
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── PWS Franchisee promo/invite email modal ──────────────────────
// Feb 2026 Phase 1. Two-step UX:
//   1. Compose  — recipient + first name inputs, live HTML preview
//   2. Confirm  — final warning + Send button
// Send calls /api/admin/pws-franchisee-interest/promo-email/send.
// Preview calls the sibling /preview endpoint on every input change.
function PromoEmailModal({ initialEmail, initialFirstName, leadId, onClose }) {
  const [toEmail, setToEmail]       = useState(initialEmail || "");
  const [firstName, setFirstName]   = useState(initialFirstName || "");
  const [phase, setPhase]           = useState("compose"); // compose | confirm | success | error
  const [busy, setBusy]             = useState(false);
  const [preview, setPreview]       = useState(null); // { subject, html, text, cta_link }
  const [previewLoading, setPreviewLoading] = useState(true);
  const [sendResult, setSendResult] = useState(null);
  const [errorMsg, setErrorMsg]     = useState("");

  // Fetch (and re-fetch on first_name change) the exact server-rendered
  // HTML so preview matches what actually gets sent.
  useEffect(() => {
    let alive = true;
    setPreviewLoading(true);
    api.get("/admin/pws-franchisee-interest/promo-email/preview", {
      params: firstName ? { first_name: firstName } : {},
    })
      .then(({ data }) => { if (alive) setPreview(data); })
      .catch(() => { /* preview is UX-only; a failure doesn't block send */ })
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [firstName]);

  const canProceed = /^\S+@\S+\.\S+$/.test((toEmail || "").trim()) && !busy;

  const runSend = async () => {
    setBusy(true);
    setErrorMsg("");
    try {
      const { data } = await api.post(
        "/admin/pws-franchisee-interest/promo-email/send",
        {
          to_email:   (toEmail || "").trim(),
          first_name: (firstName || "").trim() || null,
          lead_id:    leadId || null,
        },
      );
      setSendResult(data);
      setPhase(data.ok ? "success" : "error");
      if (data.ok) {
        toast.success(`Invite email sent to ${data.to}`);
      } else {
        toast.warning(
          "Send returned without a provider id — usually the RESEND key is missing in this env.",
        );
      }
    } catch (e) {
      setErrorMsg(formatApiError(e) || "Send failed");
      setPhase("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-pws-fi-promo-modal"
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onClose(phase === "success")}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b border-slate-200 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
            <Send className="w-4 h-4 text-cyan-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-base">
              Send PWS Franchisee Network invite
            </h3>
            <div className="text-xs text-slate-600 mt-0.5 truncate">
              {preview?.subject || "New PWS partner opportunity: Australia-wide water testing with Aware"}
            </div>
          </div>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {phase === "compose" && (
            <div data-testid="admin-pws-fi-promo-compose" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                    Recipient email
                  </label>
                  <input
                    type="email"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    placeholder="partner@example.com"
                    data-testid="admin-pws-fi-promo-to-input"
                    className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                    First name (optional)
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="e.g. Jane"
                    maxLength={80}
                    data-testid="admin-pws-fi-promo-first-name-input"
                    className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
                  />
                  <div className="text-[10px] text-slate-400 mt-1">
                    Used only for the greeting. Blank → &ldquo;Hi there&rdquo;.
                  </div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                  Preview
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 h-72 overflow-hidden">
                  {previewLoading ? (
                    <div className="flex items-center justify-center h-full text-slate-400 text-xs gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading preview…
                    </div>
                  ) : preview?.html ? (
                    <iframe
                      title="PWS Franchisee invite preview"
                      srcDoc={preview.html}
                      data-testid="admin-pws-fi-promo-preview-iframe"
                      className="w-full h-full border-0 bg-white"
                      sandbox=""
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400 text-xs">
                      Preview unavailable.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600 leading-relaxed">
                <div className="font-semibold text-slate-700 mb-1">Guardrails</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>No exact commission percentages or dollar amounts.</li>
                  <li>Wording uses &ldquo;eligible partner-attributed bookings&rdquo;.</li>
                  <li>No NATA / accreditation / government-approved claims.</li>
                  <li>Single-recipient send only. Result is logged in <code className="text-[10px] bg-white px-1 rounded">/admin/emails</code>.</li>
                </ul>
              </div>
            </div>
          )}

          {phase === "confirm" && (
            <div data-testid="admin-pws-fi-promo-confirm">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  Send this invite email to <strong className="text-amber-950">{toEmail}</strong>?
                  The email is dispatched via Resend and the result is logged to <code className="text-[11px] bg-white px-1 rounded">/admin/emails</code>.
                </div>
              </div>
            </div>
          )}

          {phase === "success" && sendResult && (
            <div data-testid="admin-pws-fi-promo-success">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-900 leading-relaxed">
                  <div className="font-semibold">Invite sent to {sendResult.to}</div>
                  {sendResult.provider_id && (
                    <div className="mt-0.5 font-mono text-[10px]">provider_id: {sendResult.provider_id}</div>
                  )}
                  <div className="mt-1">
                    Logged in the email log as <code className="text-[10px] bg-white px-1 rounded">pws_franchisee_invite</code>.
                  </div>
                </div>
              </div>
            </div>
          )}

          {phase === "error" && (
            <div data-testid="admin-pws-fi-promo-error" className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="leading-relaxed">
                <div className="font-semibold">{errorMsg || "Send failed"}</div>
                {sendResult?.category && (
                  <div className="mt-1">Logged as <code className="text-[10px] bg-white px-1 rounded">{sendResult.category}</code> anyway.</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
          {phase === "compose" && (
            <>
              <Button variant="outline" onClick={() => onClose(false)} disabled={busy} data-testid="admin-pws-fi-promo-cancel">Cancel</Button>
              <Button
                disabled={!canProceed}
                onClick={() => setPhase("confirm")}
                data-testid="admin-pws-fi-promo-review-btn"
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                Review &amp; send
              </Button>
            </>
          )}
          {phase === "confirm" && (
            <>
              <Button variant="outline" onClick={() => setPhase("compose")} disabled={busy} data-testid="admin-pws-fi-promo-back-btn">Back</Button>
              <Button
                onClick={runSend}
                disabled={busy}
                data-testid="admin-pws-fi-promo-send-btn"
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {busy ? "Sending…" : `Send to ${toEmail}`}
              </Button>
            </>
          )}
          {(phase === "success" || phase === "error") && (
            <>
              <a
                href="/admin/emails?category=pws_franchisee_invite"
                data-testid="admin-pws-fi-promo-open-email-log-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700"
              >
                <ExternalLink className="w-4 h-4" /> Email log
              </a>
              <Button
                variant="outline"
                onClick={() => onClose(phase === "success")}
                data-testid="admin-pws-fi-promo-close-btn"
              >
                Close
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

